import { AutoComplete, DatePicker, notification } from "antd";
import {
  Button,
  Checkbox,
  Input,
  Select,
  Textarea,
} from "@mantine/core";
import { IMESafeNumberInput as NumberInput } from "@/components/IMESafeNumberInput";
import {
  IconBuilding,
  IconCalculator,
  IconCalendar,
  IconCashBanknote,
  IconFileText,
  IconHash,
  IconMail,
  IconMessage,
  IconPercentage,
  IconPlus,
  IconReceipt,
  IconTrash,
  IconUsers,
  IconWallet,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

const formatDecimal = (value: number | string | undefined | null) =>
  Math.round(Number(value) || 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });

function EditAccountPayablePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const [accountPayableData, setAccountPayableData] = useState({
    name: "",
    requestedAmount: 0,
    taxRate: 0,
    partnerId: "",
    remarks: "",
  });
  const [partnerData, setPartnerData] = useState([]);
  const [bankData, setBankData] = useState([]);
  const [payableList, setPayableList] = useState<any[]>([]);
  const [deletePayableList, setDeletePayableList] = useState<string[]>([]);
  const [tempPartnerInput, setTempPartnerInput] = useState("");
  const [options, setOptions] = useState([]);

  const handleCancelButton = () => {
    navigate("/hrMainPage/accountPayable");
  };

  const handleChangePartnerData = (value, data, dataType) => {
    let match = value.match(/ID:\s*([a-zA-Z0-9-]+)/);
    let idNumber = match ? match[1] : null;
    let selectedData = data.filter((e) => e?.id == idNumber)[0];
    if (selectedData) {
      setTempPartnerInput(selectedData.name + ` (ID: ${selectedData.id})`);
      handleChangeData(selectedData.id, dataType);
    }
  };

  const handleGetPartnerData = (partnerData, text) => {
    return partnerData
      ?.map((t) => ({
        label: t?.name + ` (ID: ${t?.id})`,
        value: t?.name + ` (ID: ${t?.id})`,
      }))
      ?.filter((item) => item.label.toLowerCase().includes(text.toLowerCase()));
  };

  const getBankById = (bankId) => {
    return bankData.find((bank) => bank.id === bankId);
  };

  const handleChangeData = (value, type) => {
    setAccountPayableData((prevData) => ({
      ...prevData,
      [type]: value,
    }));
  };

  const handleChangePayableDetail = (key, value, type) => {
    setPayableList((prev) =>
      prev.map((payable) => {
        if (payable.id === key || payable.key === key) {
          return { ...payable, [type]: value };
        }
        return payable;
      })
    );
  };

  const {
    name = "",
    requestedAmount = 0,
    taxRate = 0,
    remarks = "",
  } = accountPayableData || {};

  const totalAmountWithTax = useMemo(
    () =>
      (Number(requestedAmount) || 0) +
      (Number(requestedAmount) || 0) * ((Number(taxRate) || 0) / 100),
    [requestedAmount, taxRate]
  );

  const paidAmount = useMemo(() => {
    return payableList.reduce(
      (acc, t) =>
        acc + (isNaN(parseFloat(String(t.amount))) ? 0 : parseFloat(String(t.amount))),
      0
    );
  }, [payableList]);

  const unpaidAmount = useMemo(
    () => totalAmountWithTax - paidAmount,
    [totalAmountWithTax, paidAmount]
  );

  const totalHandlingFee = useMemo(
    () => payableList.reduce((acc, t) => acc + (t.handlingFee || 0), 0),
    [payableList]
  );

  const totalDeductionAmount = useMemo(
    () => payableList.reduce((acc, t) => acc + (t.deductionAmount || 0), 0),
    [payableList]
  );

  const handleSubmit = async () => {
    try {
      for (let i = 0; i < payableList.length; i++) {
        const record = payableList[i];
        const missingFields = [];

        if (!record.paymentDate)
          missingFields.push(t("accountPayable.paymentDate") || "付款日期");
        if (!record.amount && record.amount !== 0)
          missingFields.push(t("accountPayable.paymentAmount") || "付款金額");
        if (!record.bankId)
          missingFields.push(t("accountPayable.bank") || "銀行");
        if (!record.invoiceNumber)
          missingFields.push(t("accountPayable.invoiceNumber") || "發票號碼");
        if (!record.invoiceDate)
          missingFields.push(t("accountPayable.invoiceDate") || "發票日期");

        if (missingFields.length > 0) {
          api.warning({
            message: t("accountPayable.fillRequired") || "請填寫必填欄位",
            description: (
              <div>
                {t("accountPayable.missingFields", { number: i + 1 }) ||
                  `付款紀錄 #${i + 1} 缺少以下必填欄位：`}
                <br />
                {missingFields.join("、")}
              </div>
            ),
            placement: "topLeft",
            duration: 5,
          });
          return;
        }
      }

      const result = await single(
        orpcClient.accountsPayable.update([
          {
            id: id!,
            name: accountPayableData.name,
            requestedAmount: Math.round(
              Number(accountPayableData.requestedAmount) || 0
            ),
            taxRate: Math.round(Number(accountPayableData.taxRate) || 0),
            partnerId: accountPayableData.partnerId,
            remarks: accountPayableData.remarks || undefined,
          },
        ])
      );

      if (result) {
        const updateList: any[] = [];
        const newList: any[] = [];

        payableList.forEach((r) => {
          if (r?.id && !deletePayableList.includes(r.id)) {
            updateList.push(r);
          } else if (!r?.id) {
            newList.push(r);
          }
        });

        if (deletePayableList.length) {
          await allOrThrow(
            orpcClient.accountsPayableDetail.delete(
              deletePayableList.map((d) => ({ id: d }))
            )
          );
        }

        if (updateList.length) {
          await allOrThrow(
            orpcClient.accountsPayableDetail.update(
              updateList.map((data) => ({
                id: data?.id,
                accountsPayableId: id!,
                bankId: data?.bankId,
                paymentDate: data?.paymentDate
                  ? new Date(data.paymentDate)
                  : new Date(),
                amount: Math.round(Number(data?.amount) || 0),
                invoiceNumber: data?.invoiceNumber || "",
                invoiceDate: data?.invoiceDate
                  ? new Date(data.invoiceDate)
                  : new Date(),
                paymentMethod: data?.paymentMethod || "transfer",
                remarks: data?.remarks || undefined,
                checkNumber: data?.checkNumber || undefined,
                checkClearanceDate: data?.checkClearanceDate
                  ? new Date(data.checkClearanceDate)
                  : undefined,
                handlingFee: Math.round(Number(data?.handlingFee) || 0),
                retentionAmount: Math.round(Number(data?.retentionAmount) || 0),
                deductionAmount: Math.round(Number(data?.deductionAmount) || 0),
                isSettled: data?.isSettled ?? false,
              }))
            )
          );
        }

        if (newList.length) {
          await allOrThrow(
            orpcClient.accountsPayableDetail.create(
              newList.map((data) => ({
                accountsPayableId: id!,
                bankId: data?.bankId || "",
                paymentDate: data?.paymentDate
                  ? new Date(data.paymentDate)
                  : new Date(),
                amount: Math.round(Number(data?.amount) || 0),
                invoiceNumber: data?.invoiceNumber || "",
                invoiceDate: data?.invoiceDate
                  ? new Date(data.invoiceDate)
                  : new Date(),
                paymentMethod: data?.paymentMethod || "transfer",
                remarks: data?.remarks || undefined,
                checkNumber: data?.checkNumber || undefined,
                checkClearanceDate: data?.checkClearanceDate
                  ? new Date(data.checkClearanceDate)
                  : undefined,
                handlingFee: Math.round(Number(data?.handlingFee) || 0),
                retentionAmount: Math.round(Number(data?.retentionAmount) || 0),
                deductionAmount: Math.round(Number(data?.deductionAmount) || 0),
                isSettled: data?.isSettled ?? false,
              }))
            )
          );
        }

        navigate("/hrMainPage/accountPayable");
      }
    } catch (error) {
      api.error({
        message: `Notification `,
        description: (
          <div>
            {(error as any)?.message ||
              t("accountPayable.updateFailed") ||
              "更新失敗"}
          </div>
        ),
        placement: "topLeft",
        duration: 0,
      });
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [partners, banks, ap] = await Promise.all([
          orpcClient.partner.list({}),
          orpcClient.bank.list({}),
          single(orpcClient.accountsPayable.read([{ id: id! }])),
        ]);
        setPartnerData(partners);
        setBankData(banks);

        if (ap) {
          setAccountPayableData({
            name: ap.name || "",
            requestedAmount: ap.requestedAmount || 0,
            taxRate: ap.taxRate || 0,
            partnerId: ap.partnerId || "",
            remarks: ap.remarks || "",
          });
          const partner = (partners as any[]).find((p) => p.id === ap.partnerId);
          if (partner) {
            setTempPartnerInput(`${partner.name} (ID: ${partner.id})`);
          }

          const details = (ap as any).accountsPayableDetail ?? [];
          const fetchedDetails = await allOrThrow(
            orpcClient.accountsPayableDetail.read(
              details.map((d: any) => ({ id: d.id }))
            )
          );
          setPayableList(fetchedDetails as any[]);
        }
      } catch (error) {
        console.error("Error loading account payable:", error);
      }
    };

    if (id) fetchAll();
  }, [id]);

  return (
    <div className="min-h-screen p-8">
      {contextHolder}
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div className="py-3 text-4xl font-bold" style={{ color: "#6F6F6F" }}>
          {t("accountPayable.editAccountPayable") || "編輯應付帳款"}
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <IconCashBanknote size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountPayable.totalAmountWithTax") || "總金額（含稅)"}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDecimal(totalAmountWithTax)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <IconReceipt size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountPayable.paidAmount") || "已付金額"}
                </p>
                <p className="text-lg font-semibold text-green-600">
                  {formatDecimal(paidAmount)}
                </p>
              </div>
            </div>
          </div>

          <div
            className={`rounded-lg p-4 shadow-sm ${unpaidAmount > 0 ? "border border-orange-200 bg-orange-50" : "border border-green-200 bg-green-50"}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${unpaidAmount > 0 ? "bg-orange-100" : "bg-green-100"}`}
              >
                <IconCashBanknote
                  size={20}
                  className={unpaidAmount > 0 ? "text-orange-600" : "text-green-600"}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountPayable.unpaidAmount") || "未付金額 (含稅)"}
                </p>
                <p
                  className={`text-lg font-semibold ${unpaidAmount > 0 ? "text-orange-600" : "text-green-600"}`}
                >
                  {formatDecimal(unpaidAmount)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                <IconPercentage size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountPayable.totalHandlingFee") || "總手續費"}
                </p>
                <p className="text-lg font-semibold text-purple-600">
                  {formatDecimal(totalHandlingFee)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                <IconCalculator size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountPayable.totalDeductionAmount") || "總扣款金額"}
                </p>
                <p className="text-lg font-semibold text-red-600">
                  {formatDecimal(totalDeductionAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("accountPayable.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-6 gap-4 p-6">
            <div className="col-span-3 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountPayable.accountPayableName") || "應付帳款名稱"}＊
              </label>
              <Input
                value={name}
                onChange={(e) => handleChangeData(e.target.value, "name")}
                placeholder={t("accountPayable.enterName") || "請輸入名稱"}
                leftSection={<IconMail size={16} />}
              />
            </div>

            <div className="col-span-3 space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <IconUsers size={14} />
                {t("accountPayable.partnerSupplier") || "合作方/供應商"}＊
              </label>
              <AutoComplete
                style={{ width: "100%" }}
                value={tempPartnerInput}
                placeholder={
                  t("accountPayable.enterPartnerName") || "請輸入合作方名稱"
                }
                options={options}
                onFocus={() =>
                  setOptions(handleGetPartnerData(partnerData, ""))
                }
                onSearch={(text) =>
                  setOptions(handleGetPartnerData(partnerData, text))
                }
                onChange={(e) => setTempPartnerInput(e)}
                onSelect={(e) =>
                  handleChangePartnerData(e, partnerData, "partnerId")
                }
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountPayable.requestedAmount") || "應付金額"}＊
              </label>
              <NumberInput
                value={requestedAmount}
                thousandSeparator=","
                onChange={(e) => handleChangeData(e, "requestedAmount")}
                leftSection={<IconCashBanknote size={16} />}
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountPayable.taxRate") || "稅率(%)"}＊
              </label>
              <NumberInput
                value={taxRate}
                onChange={(e) => handleChangeData(e, "taxRate")}
                leftSection={<IconPercentage size={16} />}
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountPayable.taxAmount") || "稅額"}
              </label>
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                {formatDecimal(
                  (Number(requestedAmount) || 0) *
                  ((Number(taxRate) || 0) / 100)
                )}
              </div>
            </div>

            <div className="col-span-6 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountPayable.totalAmountWithTax") || "含稅總金額"}
              </label>
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-900">
                {formatDecimal(totalAmountWithTax)}
              </div>
            </div>

            <div className="col-span-6 space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <IconMessage size={14} />
                {t("accountPayable.remarks") || "備註"}
              </label>
              <Textarea
                placeholder="請輸入備註事項..."
                value={remarks}
                onChange={(e) => handleChangeData(e.target.value, "remarks")}
                minRows={2}
                autosize
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <IconReceipt size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("accountPayable.paymentRecord") || "付款紀錄"}
              </h3>
              <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-500">
                {payableList.length} {t("accountPayable.records") || "筆"}
              </span>
            </div>
            <Button
              variant="default"
              radius="md"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={() =>
                setPayableList((prev) => [
                  ...prev,
                  {
                    paymentDate: null,
                    amount: "",
                    bankId: "",
                    paymentMethod: "transfer",
                    invoiceNumber: "",
                    invoiceDate: "",
                    checkNumber: "",
                    checkClearanceDate: null,
                    handlingFee: 0,
                    retentionAmount: 0,
                    deductionAmount: 0,
                    isSettled: false,
                    remarks: "",
                    key: uuidv4(),
                  },
                ])
              }
            >
              {t("accountPayable.addPaymentRecord") || "增加付款紀錄"}
            </Button>
          </div>

          <div className="divide-y divide-gray-100">
            {payableList.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <IconReceipt size={48} className="mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-400">
                  {t("accountPayable.noPaymentData") || "無付款資料"}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {t("accountPayable.clickToAddPayment") ||
                    "點擊上方按鈕新增付款紀錄"}
                </p>
              </div>
            )}

            {payableList.map((record, index) => (
              <div key={record.id || record.key} className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-600">
                    {t("accountPayable.paymentRecordNumber", {
                      number: index + 1,
                    }) || `付款紀錄 #${index + 1}`}
                  </span>
                  <Button
                    variant="subtle"
                    color="red"
                    size="sm"
                    radius="md"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => {
                      if (record?.id) {
                        setDeletePayableList((prev) => [...prev, record.id]);
                      }
                      setPayableList((prev) =>
                        prev.filter((r) => {
                          if (record?.id) return r.id !== record?.id;
                          return r?.key !== record?.key;
                        })
                      );
                    }}
                  >
                    {t("accountPayable.delete") || "刪除"}
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountPayable.paymentDate") || "付款日期"}＊
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        record?.paymentDate ? dayjs(record?.paymentDate) : null
                      }
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e ? e.format("YYYY-MM-DD") : null,
                          "paymentDate"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCashBanknote size={13} />
                      {t("accountPayable.paymentAmount") || "付款金額"}＊
                    </label>
                    <NumberInput
                      value={record?.amount}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e,
                          "amount"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconBuilding size={13} />
                      {t("accountPayable.bank") || "銀行"}＊
                    </label>
                    <AutoComplete
                      style={{ width: "100%" }}
                      value={
                        getBankById(record?.bankId)
                          ? `${getBankById(record?.bankId).name} (ID: ${record?.bankId})`
                          : record?.bankId || ""
                      }
                      options={bankData?.map((b) => ({
                        label: b?.name + ` (ID: ${b?.id})`,
                        value: b?.name + ` (ID: ${b?.id})`,
                      }))}
                      onChange={(value) => {
                        let match = value.match(/ID:\s*([a-zA-Z0-9-]+)/);
                        let idNumber = match ? match[1] : null;
                        if (idNumber) {
                          handleChangePayableDetail(
                            record?.id || record.key,
                            idNumber,
                            "bankId"
                          );
                        }
                      }}
                      placeholder={t("bank.enterBankName") || "請輸入銀行名稱"}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCashBanknote size={13} />
                      {t("accountPayable.paymentMethod") || "付款方式"}＊
                    </label>
                    <Select
                      value={record?.paymentMethod || "transfer"}
                      data={[
                        {
                          value: "transfer",
                          label: t("accountPayable.transfer") || "匯款",
                        },
                        {
                          value: "check",
                          label: t("accountPayable.check") || "支票",
                        },
                        {
                          value: "cash",
                          label: t("accountPayable.cash") || "現金",
                        },
                        {
                          value: "credit",
                          label: t("accountPayable.credit") || "信用卡",
                        },
                      ]}
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e,
                          "paymentMethod"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconHash size={13} />
                      {t("accountPayable.invoiceNumber") || "發票號碼"}＊
                    </label>
                    <Input
                      value={record?.invoiceNumber}
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e.target.value,
                          "invoiceNumber"
                        )
                      }
                      placeholder="INV-XXXX-XXX"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountPayable.invoiceDate") || "發票日期"}＊
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        record?.invoiceDate ? dayjs(record?.invoiceDate) : null
                      }
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e ? e.format("YYYY-MM-DD") : null,
                          "invoiceDate"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconHash size={13} />
                      {t("accountPayable.checkNumber") || "支票號碼"}
                    </label>
                    <Input
                      value={record?.checkNumber || ""}
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e.target.value,
                          "checkNumber"
                        )
                      }
                      placeholder="CHK-XXXXX"
                      disabled={record?.paymentMethod !== "check"}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountPayable.checkClearanceDate") || "兌現日期"}
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        record?.checkClearanceDate
                          ? dayjs(record?.checkClearanceDate)
                          : null
                      }
                      disabled={record?.paymentMethod !== "check"}
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e ? e.format("YYYY-MM-DD") : null,
                          "checkClearanceDate"
                        )
                      }
                    />
                  </div>

                  <div className="col-span-full my-1 border-t border-gray-100" />

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconPercentage size={13} />
                      {t("accountPayable.handlingFee") || "手續費"}
                    </label>
                    <NumberInput
                      value={record?.handlingFee ?? 0}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e,
                          "handlingFee"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconWallet size={13} />
                      {t("accountPayable.retentionAmount") || "保留款"}
                    </label>
                    <NumberInput
                      value={record?.retentionAmount ?? 0}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e,
                          "retentionAmount"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalculator size={13} />
                      {t("accountPayable.deductionAmount") || "扣款金額"}
                    </label>
                    <NumberInput
                      value={record?.deductionAmount ?? 0}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e,
                          "deductionAmount"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-gray-500">
                      {t("accountPayable.netAmount") || "實付金額"}
                    </label>
                    <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3">
                      <span
                        className={`font-semibold ${record?.isSettled ? "text-green-600" : "text-gray-900"}`}
                      >
                        {formatDecimal(
                          Number(record?.amount || 0) -
                            (record?.handlingFee || 0) -
                            (record?.retentionAmount || 0) -
                            (record?.deductionAmount || 0)
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-end pb-1">
                    <Checkbox
                      checked={record?.isSettled || false}
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e.currentTarget.checked,
                          "isSettled"
                        )
                      }
                      label={t("accountPayable.isSettled") || "已結清"}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconMessage size={13} />
                      {t("accountPayable.remarks") || "備註"}
                    </label>
                    <Textarea
                      value={record?.remarks}
                      onChange={(e) =>
                        handleChangePayableDetail(
                          record?.id || record.key,
                          e.target.value,
                          "remarks"
                        )
                      }
                      placeholder={t("accountPayable.remarks") || "備註"}
                      minRows={1}
                      autosize
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleCancelButton()}
          >
            {t("accountPayable.cancel") || "取消"}
          </Button>

          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleSubmit()}
          >
            {t("hrMain.save") || "儲存"}
          </Button>
        </div>
      </div>
    </div>
  );
}
export default EditAccountPayablePage;
